"use client"

import { useState, useEffect, useMemo } from "react"
import {
  Box, Text, Loader, Table, Group, Badge, Stack,
} from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { MarketChart, type AreaBar } from "./MarketChart"
import { getBenchmarkTableAction } from "@/actions/trading"
import type { BenchmarkRow } from "@/lib/queries"
import type { Time } from "lightweight-charts"

const FORWARD_DISCOUNTS: Record<number, number> = {
  1: 0.15,
  2: 0.20,
  3: 0.30,
  4: 0.40,
}

type SortState = { col: keyof BenchmarkRow; dir: "asc" | "desc" }

type Props = {
  spotPrice: number | null
  creditType: string
}

export function ForwardCurvesPanel({ spotPrice, creditType }: Props) {
  const t = useTranslations("trading")
  const tOrders = useTranslations("orders")
  const router = useRouter()

  const [benchmarkRows, setBenchmarkRows] = useState<BenchmarkRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<SortState>({ col: "creditType", dir: "asc" })

  useEffect(() => {
    getBenchmarkTableAction().then((rows) => {
      setBenchmarkRows(rows)
      setLoading(false)
    })
  }, [])

  const curveData = useMemo<AreaBar[]>(() => {
    if (spotPrice === null || spotPrice <= 0) return []
    const currentYear = new Date().getFullYear()
    const points: AreaBar[] = [
      { time: `${currentYear}-01-01` as Time, value: spotPrice },
    ]
    for (const [offset, discount] of Object.entries(FORWARD_DISCOUNTS)) {
      const year = currentYear + Number(offset)
      points.push({
        time: `${year}-01-01` as Time,
        value: parseFloat((spotPrice * (1 - discount)).toFixed(2)),
      })
    }
    return points
  }, [spotPrice])

  function handleSort(col: keyof BenchmarkRow) {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    )
  }

  const sorted = [...benchmarkRows].sort((a, b) => {
    const av = a[sort.col] ?? 0
    const bv = b[sort.col] ?? 0
    if (av < bv) return sort.dir === "asc" ? -1 : 1
    if (av > bv) return sort.dir === "asc" ? 1 : -1
    return 0
  })

  return (
    <Stack gap="md" p={8}>
      <Box>
        <Group justify="space-between" mb={4}>
          <Text fw={600} size="sm">{t("forwardCurve")}</Text>
          <Text size="xs" c="dimmed">{t("forwardCurveDisclaimer")}</Text>
        </Group>
        {curveData.length > 0 ? (
          <MarketChart type="area" data={curveData} height={200} />
        ) : (
          <Text c="dimmed" ta="center" size="sm">{t("noChartData")}</Text>
        )}
        <Text size="xs" c="dimmed" mt={4}>{t("indicativeForwardPrices")} · {creditType}</Text>
      </Box>

      <Box>
        <Text fw={600} size="sm" mb={8}>{t("methodology")}</Text>
        {loading ? (
          <Group justify="center"><Loader size="sm" /></Group>
        ) : sorted.length === 0 ? (
          <Text c="dimmed">{t("noChartData")}</Text>
        ) : (
          <Table highlightOnHover style={{ cursor: "pointer" }}>
            <Table.Thead>
              <Table.Tr>
                {(["creditType", "vintage", "lastClose", "change24hPct", "volume24h"] as const).map((col) => (
                  <Table.Th
                    key={col}
                    onClick={() => handleSort(col)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {col === "creditType" ? tOrders("creditType") :
                     col === "vintage" ? "Vintage" :
                     col === "lastClose" ? t("benchmarkPrice") :
                     col === "change24hPct" ? t("change24h") :
                     t("totalVolume24h")}
                    {sort.col === col ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sorted.map((row) => (
                <Table.Tr
                  key={`${row.creditType}-${row.vintage}`}
                  onClick={() =>
                    router.push(`/market/${encodeURIComponent(row.creditType)}/${row.vintage}`)
                  }
                >
                  <Table.Td>{row.creditType}</Table.Td>
                  <Table.Td>{row.vintage}</Table.Td>
                  <Table.Td>€{row.lastClose.toFixed(2)}</Table.Td>
                  <Table.Td>
                    {row.change24hPct !== null ? (
                      <Badge
                        color={row.change24hPct >= 0 ? "green" : "red"}
                        variant="light"
                        size="sm"
                      >
                        {row.change24hPct >= 0 ? "+" : ""}{row.change24hPct.toFixed(2)}%
                      </Badge>
                    ) : "N/A"}
                  </Table.Td>
                  <Table.Td>{row.volume24h.toFixed(0)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>
    </Stack>
  )
}
